/** Shared primitive types for the Hoopscope domain core. Pure TS, no imports. */

export type TeamSide = 'home' | 'away';

export const TEAM_SIDES: readonly TeamSide[] = ['home', 'away'];

export function otherSide(side: TeamSide): TeamSide {
  return side === 'home' ? 'away' : 'home';
}

export function isTeamSide(value: unknown): value is TeamSide {
  return value === 'home' || value === 'away';
}

/** A value tracked separately for each team. */
export interface PerSide<T> {
  home: T;
  away: T;
}

/** Which of a team's two jersey sets it wears in a game (R16). */
export type JerseyKind = 'light' | 'dark';

/** A player as a game knows them. Nothing else about a player may ever reach an event (§5.6). */
export interface GamePlayer {
  name: string;
  number: string;
  captain?: boolean;
}

export interface TeamInfo {
  /** Id of the saved team this game was set up from, if any. */
  id?: string;
  name: string;
  shortName?: string;
  /** Colour worn in this game, as a CSS colour string ('#ff6600'). The core never interprets it. */
  color?: string;
  /** Which jersey set `color` comes from. */
  kit?: JerseyKind;
  /** Who is listed for this game. Missing for games without a player list. */
  roster?: readonly GamePlayer[];
}

/** Longest colour string accepted from a command ('#RRGGBB', 'rgb(...)' and so on). */
export const MAX_COLOR_LENGTH = 32;

export function isJerseyKind(value: unknown): value is JerseyKind {
  return value === 'light' || value === 'dark';
}

export interface Teams {
  home: TeamInfo;
  away: TeamInfo;
}

/** Where an action came from (§6.2). Stored on every event. */
export interface EventSource {
  adapter: string;
  deviceId?: string;
}

export type ScorePoints = 1 | 2 | 3;

export function isScorePoints(value: unknown): value is ScorePoints {
  return value === 1 || value === 2 || value === 3;
}

/**
 * Foul types. FIBA names: personal, shooting (a personal foul on a shooter), offensive,
 * technical, unsportsmanlike, disqualifying. NBA names: flagrant1, flagrant2.
 */
export type FoulType =
  | 'personal'
  | 'shooting'
  | 'offensive'
  | 'technical'
  | 'disruptive'
  | 'flagrant'
  | 'disqualifying'
  // Older games only (before the FIBA 2026 rules): kept so they still replay.
  | 'unsportsmanlike'
  | 'flagrant1'
  | 'flagrant2';

export const FOUL_TYPES: readonly FoulType[] = [
  'personal',
  'shooting',
  'offensive',
  'technical',
  'disruptive',
  'flagrant',
  'disqualifying',
  'unsportsmanlike',
  'flagrant1',
  'flagrant2',
];

/** Foul types the scorer can choose: the FIBA 2026 names (Art. 34-39). */
export const ACTIVE_FOUL_TYPES: readonly FoulType[] = [
  'personal',
  'shooting',
  'offensive',
  'technical',
  'disruptive',
  'flagrant',
  'disqualifying',
];

export function isFoulType(value: unknown): value is FoulType {
  return typeof value === 'string' && (FOUL_TYPES as readonly string[]).indexOf(value) !== -1;
}

/** Jersey numbers "0"–"99" plus "00" (no leading zeros otherwise, e.g. "07" is invalid). */
export function isValidPlayerNumber(value: unknown): value is string {
  return typeof value === 'string' && /^(?:00|\d|[1-9]\d)$/.test(value);
}
